import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const pick = (items) => items[Math.floor(Math.random() * items.length)];

const VARIANTS = [
  [-4, -1, 0, 2, 3], [-4, 2, 1, 3, 2], [-3, 1, -2, 4, 5],
  [-3, 2, 2, 5, 3], [-2, 1, 3, 2, 4], [-2, 3, 0, 5, 2],
  [-1, 2, 2, 4, 5], [-1, 4, 0, 3, 2], [1, -4, 0, 2, 3],
  [1, 3, 1, 5, 4], [2, -3, 0, 4, 2], [2, 4, -1, 3, 5],
  [3, -2, 2, 2, 4], [3, 1, -3, 5, 2], [4, -1, 1, 3, 4],
].map(([r1, r2, targetSum, leadCoeff, linearCoeff]) => ({
  r1, r2, targetSum, leadCoeff, linearCoeff,
}));

function shuffle(items) {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function signed(value) {
  return value >= 0 ? `+${value}` : `${value}`;
}

function factorForRoot(root, coefficient = 1) {
  const constant = -coefficient * root;
  const xPart = coefficient === 1 ? "x" : `${coefficient}x`;
  if (constant === 0) return xPart;
  return `${xPart}${signed(constant)}`;
}

function makeOptions(answer, fixedOptions) {
  if (fixedOptions) {
    const options = fixedOptions.map(([label, value]) => ({
      label, value, latex: String(value), isCorrect: value === answer,
    }));
    return { options, correctIndex: options.findIndex((item) => item.isCorrect) };
  }

  const candidates = [answer, -answer, answer + 2, answer - 2, answer + 1, answer - 1];
  const values = [];
  for (const value of candidates) {
    if (!values.includes(value)) values.push(value);
    if (values.length === 4) break;
  }
  const options = shuffle(values).map((value, index) => ({
    label: LETTERS[index], value, latex: String(value), isCorrect: value === answer,
  }));
  return { options, correctIndex: options.findIndex((item) => item.isCorrect) };
}

function buildTask(item, source) {
  const parameter = item.targetSum - item.r1 - item.r2;
  const factor1 = factorForRoot(item.r1);
  const factor2 = "x-m";
  const factor3 = factorForRoot(item.r2, item.linearCoeff);
  const equationLatex = `${item.leadCoeff}(${factor1})(${factor2})(${factor3})=0`;
  const fixed = source.variant === "original"
    ? [["A", -7], ["B", 2], ["C", 5], ["D", 7]]
    : null;
  const { options, correctIndex } = makeOptions(parameter, fixed);

  return {
    type: "MaturaFactoredRootsSumParameterClosedTask",
    source,
    tags: ["matura", "rownania wielomianowe", "parametr", "postac iloczynowa"],
    data: {
      ...item,
      parameter,
      factor1,
      factor2,
      factor3,
      equationLatex,
      options,
      correctIndex,
      maxPoints: 1,
    },
  };
}

export function generateTask(options = {}) {
  const original = options.variant === "original" && options.session === "2026_MAJ";
  if (original) {
    return buildTask(
      { r1: -3, r2: -2, targetSum: 0, leadCoeff: 3, linearCoeff: 2 },
      { variant: "original", exam: "MATURA", session: "2026_MAJ", taskNumber: "8" }
    );
  }
  return buildTask(pick(VARIANTS), {
    variant: "random",
    basedOn: "MATURA_2026_MAJ_8",
    taskNumber: "8",
  });
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Dane jest równanie <InlineMath math={data.equationLatex} />, w którym
        niewiadomą jest <InlineMath math="x" />, natomiast <InlineMath math="m" />{" "}
        jest parametrem rzeczywistym. Suma wszystkich rozwiązań tego równania
        jest równa <InlineMath math={String(data.targetSum)} />. Parametr{" "}
        <InlineMath math="m" /> jest równy:
      </p>
      <div style={{ display: "grid", gap: 6 }}>
        {data.options.map((option) => (
          <div key={option.label}><InlineMath math={`${option.label}.\\;${option.latex}`} /></div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({ value: index, label: option.label, textNode: option.label }))}
      value={Number.isInteger(userAnswer?.choiceIndex) ? userAnswer.choiceIndex : null}
      onChange={(choiceIndex) => onChange?.({
        choiceIndex,
        label: data.options[choiceIndex].label,
        value: data.options[choiceIndex].value,
      })}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return Number.isInteger(userAnswer?.choiceIndex) && userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const answer = data.options[data.correctIndex];
  return <InlineMath math={`${answer.label}.\\;m=${answer.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!Number.isInteger(userAnswer?.choiceIndex)) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${userAnswer.label}.\\;m=${userAnswer.value}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p><strong>Krok 1.</strong> Odczytujemy rozwiązania z postaci iloczynowej.</p>
      <BlockMath math={data.equationLatex} />
      <BlockMath math={`
        \\begin{aligned}
          ${data.factor1}=0 &\\Longrightarrow x=${data.r1},\\\\
          x-m=0 &\\Longrightarrow x=m,\\\\
          ${data.factor3}=0 &\\Longrightarrow x=${data.r2}.
        \\end{aligned}
      `} />
      <BlockMath math={`x_1=${data.r1},\\qquad x_2=m,\\qquad x_3=${data.r2}`} />
      <p>
        Stały czynnik <InlineMath math={String(data.leadCoeff)} /> nie wpływa na
        rozwiązania równania.
      </p>
      <p><strong>Krok 2.</strong> Korzystamy z podanej sumy wszystkich rozwiązań.</p>
      <BlockMath math={`${data.r1}+m${signed(data.r2)}=${data.targetSum}`} />
      <BlockMath math={`m${signed(data.r1 + data.r2)}=${data.targetSum}`} />
      <BlockMath math={`m=${data.parameter}`} />
      <p><strong>Wniosek.</strong> <InlineMath math={`m=${data.parameter}`} />.</p>
    </div>
  );
}
